import { formatAmount, formatDateForItem } from 'utils';
import { accountDisputeFields, addressLinkDisputeFields, aliasDisputeFields, associateDisputeFields, DisputeTypeEnum, electoralRollDisputeFields, insolvencyDisputeFields, insolvencyDisputeType, judgmentDisputeFields, searchDisputeFields, } from './dispute';
import { getElectoralRollStatusLabel } from '.';
const safeFormatDate = (value) => {
    if (!value)
        return undefined;
    try {
        return formatDateForItem(value);
    }
    catch {
        return value;
    }
};
const findAccountById = (data, accountId) => {
    if (accountId == null)
        return undefined;
    return data.accounts?.find((account) => account.accountId === accountId);
};
const findSearchByReference = (data, searchReference) => {
    if (!searchReference)
        return undefined;
    const matches = (search) => search.searchReference === searchReference;
    return data.searches?.hard.find(matches) ?? data.searches?.soft.find(matches);
};
const buildAccountDisputeItem = (account) => ({
    label: 'Account',
    description: account.lenderName ?? account.accountTypeName ?? 'Account',
    disputeType: DisputeTypeEnum.accounts,
    fields: accountDisputeFields(account),
    iconUrl: account.iconUrl,
    lenderName: account.lenderName,
    rightLabel: account.balance !== null && account.currencyCode
        ? formatAmount(account.balance, account.currencyCode)
        : undefined,
});
const buildSearchDisputeItem = (search) => ({
    label: 'Search',
    description: search.company ?? search.searchReference ?? undefined,
    disputeType: DisputeTypeEnum.searches,
    fields: searchDisputeFields(search),
    iconUrl: search.iconUrl,
    lenderName: search.company,
    subtitle: safeFormatDate(search.searchDate),
});
const buildJudgmentDisputeItems = (judgments) => (judgments ?? [])
    .filter((judgment) => !!judgment && judgment.casePerId != null)
    .map((judgment, index) => ({
    label: `Judgment ${index + 1}`,
    description: judgment.caseNumber ?? judgment.courtName ?? undefined,
    disputeType: DisputeTypeEnum.judgments,
    fields: judgmentDisputeFields(judgment),
}));
const buildInsolvencyDisputeItems = (insolvencies) => (insolvencies ?? [])
    .filter((insolvency) => !!insolvency && insolvency.insolvencyOrderId != null)
    .map((insolvency, index) => ({
    label: `Insolvency ${index + 1}`,
    description: insolvency.orderTypeName ?? insolvency.caseReference ?? undefined,
    disputeType: insolvencyDisputeType(insolvency),
    fields: insolvencyDisputeFields(insolvency),
}));
const buildAddressLinkDisputeItems = (links) => (links ?? [])
    .filter((link) => !!link && link.addressLinkId != null)
    .map((link, index) => ({
    label: `Address link ${index + 1}`,
    description: link.addressFrom || link.addressTo || undefined,
    disputeType: DisputeTypeEnum.addressLinks,
    fields: addressLinkDisputeFields(link),
}));
const buildFinancialConnectionDisputeItems = (connections) => (connections ?? [])
    .filter((connection) => !!connection && connection.associateId != null)
    .map((connection, index) => ({
    label: `Connection ${index + 1}`,
    description: connection.name ?? undefined,
    disputeType: DisputeTypeEnum.financialConnections,
    fields: associateDisputeFields(connection),
}));
const buildOtherNameDisputeItems = (names) => (names ?? [])
    .filter((name) => !!name && name.aliasId != null)
    .map((name, index) => ({
    label: `Name ${index + 1}`,
    description: name.alias ?? name.linkName ?? undefined,
    disputeType: DisputeTypeEnum.otherNames,
    fields: aliasDisputeFields(name),
}));
const buildElectoralDisputeItem = (electoralRoll) => {
    if (!electoralRoll)
        return null;
    const isFound = electoralRoll.currentStatus !== 'NotFound';
    return {
        label: 'Electoral register',
        description: getElectoralRollStatusLabel(electoralRoll.currentStatus) ?? undefined,
        disputeType: isFound
            ? DisputeTypeEnum.electoralRoll.record
            : DisputeTypeEnum.electoralRoll.noRecord,
        fields: isFound ? electoralRollDisputeFields(electoralRoll) : {},
    };
};
export const buildDisputeItems = (data, params) => {
    switch (params.category) {
        case 'account': {
            const account = findAccountById(data, params.accountId);
            return account ? [buildAccountDisputeItem(account)] : [];
        }
        case 'search': {
            const search = findSearchByReference(data, params.searchReference);
            return search ? [buildSearchDisputeItem(search)] : [];
        }
        case 'judgments':
            return buildJudgmentDisputeItems(data.judgments);
        case 'insolvencies':
            return buildInsolvencyDisputeItems(data.insolvencies);
        case 'addressLinks':
            return buildAddressLinkDisputeItems(data.addressLinks);
        case 'financialConnections':
            return buildFinancialConnectionDisputeItems(data.financialConnections);
        case 'otherNames':
            return buildOtherNameDisputeItems(data.otherNames);
        case 'electoralRoll': {
            const item = buildElectoralDisputeItem(data.electoralRoll);
            return item ? [item] : [];
        }
        default:
            return [];
    }
};
